import { createServiceRequest } from '@/services/http/createServiceRequest';

const request = createServiceRequest('crm');
const BASE = '/bff/zy-crm';

export type CustomerItem = {
  uuid: string;
  name?: string | null;
  ownerUserUuid?: string | null;
  ownerUserName?: string | null;
  ownerName?: string | null;
  ownerMemberName?: string | null;
  mobile?: string | null;
  telephone?: string | null;
  email?: string | null;
  qq?: string | null;
  wechat?: string | null;
  remark?: string | null;
  followUpStatus?: number | string | null;
  lockStatus?: boolean;
  dealStatus?: boolean;
  inPool?: boolean;
  ownerTime?: string | null;
  contactLastTime?: string | null;
  contactNextTime?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  [key: string]: unknown;
};

export type QueryCustomersParams = {
  page?: number;
  pageSize?: number;
  keyword?: string;
  ownerUserUuid?: string;
  sortBy?: string;
  sortOrder?: string;
};

export type CreateCustomerParams = {
  name: string;
  mobile?: string;
  telephone?: string;
  email?: string;
  qq?: string;
  wechat?: string;
  remark?: string;
  ownerUserUuid?: string;
  ownerTime?: string;
  followUpStatus?: number | string;
  lockStatus?: boolean;
  dealStatus?: boolean;
};

export type UpdateCustomerParams = Partial<CreateCustomerParams>;

export type QueryCustomersResult = {
  list: CustomerItem[];
  total: number;
  pageNo: number;
  pageSize: number;
};

type CrmListResponse = Partial<QueryCustomersResult> & {
  data?: unknown;
  items?: unknown;
  records?: unknown;
  rows?: unknown;
  content?: unknown;
  count?: number;
};

type CrmCustomerResponse = CustomerItem & {
  data?: unknown;
  customer?: unknown;
};

const buildQueryCustomersParams = (params?: QueryCustomersParams) => {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries({
      page: params.page,
      pageSize: params.pageSize,
      keyword: params.keyword,
      ownerUserUuid: params.ownerUserUuid,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    }).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
};

const readCustomerList = (payload: CrmListResponse | CustomerItem[]) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const candidates = [
    payload.list,
    payload.items,
    payload.records,
    payload.rows,
    payload.content,
  ];

  return candidates.find(Array.isArray) || [];
};

const normalizeQueryCustomersResult = (
  response: CrmListResponse | CustomerItem[],
  fallbackParams?: QueryCustomersParams,
): QueryCustomersResult => {
  const payload =
    !Array.isArray(response) &&
    response.data &&
    typeof response.data === 'object'
      ? (response.data as CrmListResponse)
      : response;
  const list = readCustomerList(payload);
  const pageSize =
    !Array.isArray(payload) && typeof payload.pageSize === 'number'
      ? payload.pageSize
      : fallbackParams?.pageSize || list.length;

  return {
    list,
    total:
      !Array.isArray(payload) && typeof payload.total === 'number'
        ? payload.total
        : !Array.isArray(payload) && typeof payload.count === 'number'
          ? payload.count
          : list.length,
    pageNo:
      !Array.isArray(payload) && typeof payload.pageNo === 'number'
        ? payload.pageNo
        : fallbackParams?.page || 1,
    pageSize,
  };
};

const normalizeCustomer = (response: CrmCustomerResponse): CustomerItem => {
  const payload =
    response.data && typeof response.data === 'object'
      ? response.data
      : response.customer && typeof response.customer === 'object'
        ? response.customer
        : response;

  return payload as CustomerItem;
};

export async function queryCustomers(
  params?: QueryCustomersParams,
): Promise<QueryCustomersResult> {
  const response = await request<CrmListResponse | CustomerItem[]>(
    `${BASE}/customers`,
    {
      method: 'GET',
      params: buildQueryCustomersParams(params),
    },
  );

  return normalizeQueryCustomersResult(response, params);
}

export async function createCustomer(
  data: CreateCustomerParams,
): Promise<CustomerItem> {
  return request<CustomerItem>(`${BASE}/customers`, {
    method: 'POST',
    data,
  });
}

export async function updateCrmCustomer(
  customerUuid: string,
  data: UpdateCustomerParams,
): Promise<CustomerItem> {
  const response = await request<CrmCustomerResponse>(
    `${BASE}/customers/${customerUuid}`,
    {
      method: 'PUT',
      data,
    },
  );

  return normalizeCustomer(response);
}

export async function getCrmCustomer(
  customerUuid: string,
): Promise<CustomerItem> {
  const response = await request<CrmCustomerResponse>(
    `${BASE}/customers/${customerUuid}`,
    {
      method: 'GET',
    },
  );

  return normalizeCustomer(response);
}
